import { GigReportStepsQuery, GigReportOverviewFragment, ReportSubgroupType } from "gql-gen";

type GigReportStep = NonNullable<NonNullable<GigReportStepsQuery["gigReportSteps"]>[0]>;
type Question = GigReportStep["questions"][0];

// Type matching the current Group structure used by FieldReportView
export type ParsedGroup = GigReportOverviewFragment["report"][0];
type ParsedSubgroup = ParsedGroup["subgroups"][0];
type ParsedQuestion = ParsedSubgroup["questions"][0];

interface QuestionState {
  state: "answered" | "unasked" | "na" | "pending";
  value: any;
  defaultValue?: any;
  answerType?: string;
}

/**
 * Parse the state JSON from gigReportSteps
 * State structure: { questionId: { state: "answered"|"unasked"|"na", value: any } }
 */
function parseState(stateJson: string | Record<string, QuestionState>): Record<string, QuestionState> {
  // If it's already an object, return it
  if (typeof stateJson === "object" && stateJson !== null) {
    return stateJson as Record<string, QuestionState>;
  }

  // Otherwise try to parse JSON string
  try {
    return JSON.parse(stateJson as string);
  } catch (e) {
    console.error("Failed to parse gigReportStep state:", e);
    return {};
  }
}

/**
 * Convert a GigReportStep question + state to the format expected by FieldReportView
 */
function convertQuestion(question: Question, state: Record<string, QuestionState>, productId: string | null): ParsedQuestion {
  // State keys can be in format "questionId_productId" or "questionId_null"
  const stateKey = `${question.id}_${productId || "null"}`;
  const questionState = state[stateKey] || state[question.id];

  // Map state from gigReportSteps to format expected by FieldReportView
  // "pending" means shown but not answered yet
  // "unasked" means conditional question that wasn't shown
  let answerState: string | null = null;
  if (questionState) {
    if (questionState.state === "answered") {
      answerState = "answered";
    } else if (questionState.state === "na") {
      answerState = "na";
    } else if (questionState.state === "unasked") {
      answerState = "unasked";
    } else if (questionState.state === "pending") {
      answerState = null;
    }
  }

  return {
    id: question.id,
    shortTitle: question.shortTitle || question.gogetterTitle || "",
    gogetterTitle: question.gogetterTitle || "",
    columnTitle: question.columnTitle || null,
    answerState: answerState,
    answerType: question.answerType,
    answerJson: questionState?.value !== undefined ? JSON.stringify(questionState.value) : null,
    notApplicableDesc: question.notApplicableDesc,
    __typename: "ReportGroupQuestion" as const,
  };
}

/**
 * Transform gigReportSteps data into the report structure expected by FieldReportView
 *
 * This creates a single "subgroup" per step with type "default" containing all questions.
 * The step becomes a "Group" with the step's name as the group name.
 */
export function parseGigReportSteps(
  gigReportSteps: GigReportStepsQuery["gigReportSteps"],
  products: GigReportOverviewFragment["products"]
): ParsedGroup[] {
  if (!gigReportSteps) {
    return [];
  }

  return gigReportSteps
    .filter((step): step is GigReportStep => step !== null)
    .map((step) => {
      const state = parseState(step.state);

      const product = step.productId
        ? products.find((p) => p.id === step.productId)
        : null;

      // A pin_output question is form configuration the engine answers with
      // the PIN it creates; it has no row in the overview.
      const questions: ParsedQuestion[] = step.questions
        .filter((question) => (question.answerType as string) !== "pin_output")
        .map((question) => convertQuestion(question, state, step.productId || null));

      const subgroup: ParsedSubgroup = {
        id: step.id,
        name: step.name,
        type: ReportSubgroupType.Simple,
        questions,
        __typename: "ReportSubgroup" as const,
      };

      const group: ParsedGroup = {
        id: step.id,
        name: step.name,
        productId: step.productId,
        productName: product?.profilePictures?.[0]?.url ? undefined : step.name,
        subgroups: [subgroup],
        __typename: "ReportGroup" as const,
      };

      return group;
    });
}
